// shared-lib/cabinet-runs/vanitySinkBay.js
//
// How wide a vanity's sink bays are — the ONE rule, asked for the whole run at
// once, with the sizing intent as its argument (`sizing.js`).
//
// It takes ALL the basins rather than one at a time because the answer is not
// per-basin: what a bay may claim is bounded by its NEIGHBOUR as much as by the
// run's end, and whether the leftovers are worth building is a question about
// the whole run. Asking per basin is what left a 63.6" double vanity as two 24"
// bases and three scribes — see below.
//
// Two callers, because the answer has to be decided twice: once by
// `vanityToRun` when it derives a run, and again by the depth picker when the
// user changes the intent, since a bay's width is STORED in the run's spec and
// the fill only reads it. Extracted rather than duplicated for the obvious
// reason — two copies of "what is a sink base" is how the profile's 1" and the
// scan path's 1.25" came to be two answers for one overhang.
//
// Pure. No I/O.

const {
  sinkBaseWidthFor,
  sinkBaseFromWidths,
  SINK_BASE_WIDTHS_IN,
  SINK_BASE_SIDE_ALLOWANCE_IN,
} = require('./bays');
const { kitchenSinkBaseWidthsIn } = require('./sinkBaseWidths');
const {
  familyById,
  familyOf,
  familyFillOpts,
  narrowestModuleIn,
} = require('./families');
const { familySizes, vanitySinkWidthsIn } = require('./vendorSizes');
const { DOUBLE_BOWL_UNIT_WHEN_BOTH_FIT } = require('./doubleBowlUnit');
const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
const { AS_BUILT, STANDARD, sizingOf } = require('./sizing');
const { getFixtureGroupSinks } = require('../vanity-assembly/fixtureGroup');

const IN = 0.0254;
// A sixteenth of a millimetre: two lengths the user would call the same.
const EPS = 1e-9;
// A thousandth of a millimetre: two totals of filler that are the same.
const TIE = 1e-6;
/** A length as a sentence says it: 19", 20.5". */
const said = (m) => `${+(m / IN).toFixed(1)}"`;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
// A generic family's vanity sink base is never narrower than the standard 24"
// nor wider than one module, 36", whatever else the family sells.
const GENERIC_SINK_MIN_IN = SINK_BASE_WIDTHS_IN[0];
const GENERIC_SINK_MAX_IN = SINK_BASE_WIDTHS_IN[SINK_BASE_WIDTHS_IN.length - 1];
// The step a family that builds any width (Custom) is asked about.
const CONTINUOUS_STEP = 0.5 * IN;

/**
 * Each basin's TERRITORY: the stretch of run that basin's cabinetry may claim,
 * divided from its neighbours at the MIDPOINT BETWEEN THE BOWLS (user ruling
 * 2026-09-26) and bounded by the run's own ends.
 *
 * The midpoint rather than equal shares, because it is the bowls that are
 * fixed: a scanned vanity's basins are incumbents the run never re-emits, so
 * dividing between them is the one split that cannot disagree with where the
 * water actually is.
 */
function territoriesFor(sorted, lo, hi) {
  return sorted.map((b, i) => ({
    lo: i === 0 ? lo : (sorted[i - 1].centerParam + b.centerParam) / 2,
    hi:
      i === sorted.length - 1
        ? hi
        : (b.centerParam + sorted[i + 1].centerParam) / 2,
  }));
}

/** How wide a base centred on a bowl at `centerParam` may be in `territory`. */
const roomAround = (centerParam, territory) =>
  2 * Math.min(centerParam - territory.lo, territory.hi - centerParam);

/**
 * The narrowest base that clears this bowl (`floor`), inside this territory:
 * capped at what it holds centred on the bowl, floored at the bowl itself.
 */
function standardBaseIn(bowl, centerParam, territory, floor) {
  return Math.min(
    territory.hi - territory.lo,
    Math.max(bowl, Math.min(floor, roomAround(centerParam, territory)))
  );
}

/**
 * The sink bases a STANDARD vanity of this run's family comes in (inches,
 * narrowest first), or null for a family that builds any width (Custom):
 * a line's own vanity sink units (Venus), else the family's box widths from
 * the standard 24" to one module, 36" — Semi-Custom's 3" steps, Stock's list
 * (user ruling 2026-11-03: "build what the family choice allows").
 *
 * A run that NAMES no family takes the default family's sizes — never the
 * scan's Custom default (`familyOf`): a scanned run reads as Custom because a
 * measured cabinet is whatever width it was built (user ruling 2026-10-08),
 * and asking it for STANDARD sizes would answer with the scan's own width
 * again — one 35.9" box for a 35.9" vanity, the pick doing nothing. A family
 * the company chose, Custom included, is asked for its own.
 */
function vanitySinkSizesIn(run) {
  const family = familyById(run?.family) || familyOf(null);
  if (familySizes(family)) return vanitySinkWidthsIn(family, 1);
  if (family?.spec?.fill === 'continuous') return null;
  const sold = (family?.spec?.widths || []).filter(
    (w) => w >= GENERIC_SINK_MIN_IN && w <= GENERIC_SINK_MAX_IN
  );
  return sold.length ? sold : SINK_BASE_WIDTHS_IN;
}

/**
 * The narrowest of `sizesIn` that clears a bowl `bowlWidth` wide by
 * `SINK_BASE_SIDE_ALLOWANCE_IN` a side (metres) — with no list, the bowl plus
 * that, between the generic 24" and 36".
 */
function sinkFloorFor(sizesIn, bowlWidth) {
  if (sizesIn) return sinkBaseFromWidths(bowlWidth, sizesIn);
  const needed = (bowlWidth || 0) / IN + SINK_BASE_SIDE_ALLOWANCE_IN;
  return clamp(needed, GENERIC_SINK_MIN_IN, GENERIC_SINK_MAX_IN) * IN;
}

/** The stretches a set of spans leaves bare: both run ends, and between. */
function gapsOf(spans, lo, hi) {
  if (!spans.length) return [];
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  return [
    sorted[0].start - lo,
    ...sorted.slice(1).map((s, i) => s.start - sorted[i].end),
    hi - sorted[sorted.length - 1].end,
  ].filter((g) => g > EPS);
}

/**
 * How much of a run's ends and joints — metres — is too narrow for any box
 * the fill packs (`minBox`): what it can only close with filler.
 */
const fillerOf = (spans, lo, hi, minBox) =>
  gapsOf(spans, lo, hi)
    .filter((g) => g < minBox - EPS)
    .reduce((sum, g) => sum + g, 0);

/** The span a base `width` wide centred at `centerParam` covers. */
const spanOf = (width, centerParam) => ({
  start: centerParam - width / 2,
  end: centerParam + width / 2,
});

/**
 * The bases that leave the LEAST FILLER, the narrower on a tie (user ruling
 * 2026-11-03) — a narrower base leaves room for real cabinets beside it.
 *
 * Each slot is one base: the narrowest it may be (`floor`), the widest
 * (`room`) and where a base of a given width stands (`place`). It may take
 * `floor` or any of `sizes` (metres, narrowest first) between the two; with
 * no list (a family that builds any width) every `CONTINUOUS_STEP` between
 * them and `room` itself, up to 36". One width is asked of every base at a
 * time, each taking the widest of its own no wider: a vanity's bases are one
 * size, and asking widths rather than every combination keeps a three-bowl
 * vanity as cheap as a double.
 *
 * @returns {number[]} a width per slot.
 */
function leastFillerWidths(slots, sizes, lo, hi, minBox) {
  const choicesOf = (slot) => {
    if (sizes) {
      const fit = sizes.filter(
        (w) => w > slot.floor + EPS && w <= slot.room + EPS
      );
      return [slot.floor, ...fit];
    }
    const top = Math.min(slot.room, GENERIC_SINK_MAX_IN * IN);
    const steps = [slot.floor];
    for (let w = slot.floor + CONTINUOUS_STEP; w < top - EPS; ) {
      steps.push(w);
      w += CONTINUOUS_STEP;
    }
    if (top > slot.floor + EPS) steps.push(top);
    return steps;
  };
  const choices = slots.map(choicesOf);
  const asked = [...new Set(choices.flat())].sort((a, b) => a - b);
  let best = null;
  for (const target of asked) {
    const widths = choices.map((own) => {
      const fit = own.filter((w) => w <= target + EPS);
      return fit.length ? fit[fit.length - 1] : own[0];
    });
    const spans = slots.map((s, i) => spanOf(widths[i], s.place(widths[i])));
    const filler = fillerOf(spans, lo, hi, minBox);
    if (!best || filler < best.filler - TIE) best = { filler, widths };
  }
  return best.widths;
}

// ─── A vanity built in a cabinet LINE (2026-10-21) ──────────────────────────
//
// A line with a size catalog (Venus) sells its vanity sink bases at its own
// widths, and one unit for two bowls. A vanity the bathroom generator planned
// in such a line used to be SNAPPED to the widest one sink unit that fit
// (design 2026-10-05, D6) — an 88.7" double built as a 60" box with 14" of
// bare wall either side. It is built as a run over the whole planned width
// instead, and these are that run's sink bases; the fill makes the rest
// (drawer and door bases, fillers, one counter).

/**
 * The line a STANDARD vanity run is built in — a family with a size catalog
 * — or null: a kitchen run, an as-built vanity, a generic family.
 */
function vanityLineOf(run, sizing = sizingOf(run)) {
  if (profileOf(run).id !== VANITY_PROFILE_ID || sizing === AS_BUILT) {
    return null;
  }
  const family = familyOf(run);
  return familySizes(family) ? family : null;
}

/**
 * The narrowest sink base that fits a bowl `bowlWidth` wide on this run: the
 * bowl plus `SINK_BASE_SIDE_ALLOWANCE_IN` a side, in the sizes a STANDARD
 * vanity of its family comes in (`vanitySinkSizesIn` — Venus and Semi-Custom
 * sell a 27" base, where the standard list jumps from 24" to 30"), the widest
 * it makes for a bowl wider than all of them; an as-built vanity's on the
 * standard list it always used, a kitchen run's in its line's sizes up to 36"
 * (`sinkBaseWidths.js`). ONE answer for the plan below and for the fill,
 * which widens a sink bay to fit its bowl (`bayFootprint`): two answers would
 * have the fill grow every 27" unit the plan chose into a 30" one.
 */
function sinkBaseWidthForRun(run, bowlWidth) {
  if (profileOf(run).id === VANITY_PROFILE_ID) {
    return sizingOf(run) === AS_BUILT
      ? sinkBaseWidthFor(bowlWidth)
      : sinkFloorFor(vanitySinkSizesIn(run), bowlWidth);
  }
  const widths = kitchenSinkBaseWidthsIn(familyOf(run));
  return widths.length
    ? sinkBaseFromWidths(bowlWidth, widths)
    : sinkBaseWidthFor(bowlWidth);
}

/** The narrowest box the fill packs into what a run's sink bases leave. */
function narrowestFillBox(run) {
  const widths = familyFillOpts(run).allowedWidthsIn;
  return (widths?.length ? Math.min(...widths) : narrowestModuleIn(run)) * IN;
}

/**
 * Both bowls in the line's double-bowl unit `width` wide, centred on them —
 * or null where they do not both fit inside it as they stand, or one is wider
 * than the half it would sit in. The unit seats each bowl in the middle of its
 * own half, which is where its compartments are.
 */
function doubleUnit(sorted, lo, hi, width) {
  if (hi - lo < width - EPS) return null;
  const mid = (sorted[0].centerParam + sorted[1].centerParam) / 2;
  const centerParam = clamp(mid, lo + width / 2, hi - width / 2);
  const inside = sorted.every(
    (b) =>
      b.centerParam - b.bowl / 2 >= centerParam - width / 2 - EPS &&
      b.centerParam + b.bowl / 2 <= centerParam + width / 2 + EPS &&
      b.bowl <= width / 2 + EPS
  );
  if (!inside) return null;
  return {
    basins: sorted,
    width,
    centerParam,
    seats: [centerParam - width / 4, centerParam + width / 4],
  };
}

/**
 * One bowl's narrowest single sink unit: the base the bowl needs in the
 * line's sizes (`sinkBaseWidthForRun`), else — where its territory cannot
 * hold that — the widest the line makes that holds the bowl at all, which is
 * the standard rule's cap. Centred on the bowl and inside its territory, as
 * the standard rule is. `{ refusedRoom }` when the line makes nothing that
 * fits.
 */
function singleUnit(b, t, widths, run) {
  const room = roomAround(b.centerParam, t);
  const fits = widths.filter((w) => w <= room + EPS && w >= b.bowl - EPS);
  if (!fits.length) return { refusedRoom: room };
  const wanted = sinkBaseWidthForRun(run, b.bowl);
  const width = fits.find((w) => w >= wanted - EPS) ?? fits[fits.length - 1];
  return {
    basins: [b],
    width,
    centerParam: b.centerParam,
    seats: [b.centerParam],
  };
}

/** What a set of units leaves the fill to close with filler. */
const unitsFiller = (units, lo, hi, minBox) =>
  fillerOf(
    units.map((u) => spanOf(u.width, u.centerParam)),
    lo,
    hi,
    minBox
  );

/** The line's narrowest double-bowl unit both bowls fit inside, or null. */
function lineDoubleUnit(sorted, lo, hi, family) {
  if (!DOUBLE_BOWL_UNIT_WHEN_BOTH_FIT || sorted.length !== 2) return null;
  for (const wIn of vanitySinkWidthsIn(family, 2)) {
    const unit = doubleUnit(sorted, lo, hi, wIn * IN);
    if (unit) return unit;
  }
  return null;
}

/**
 * One single sink unit per bowl, in its own territory (divided at the
 * midpoint between the bowls), each centred on its bowl: the narrowest that
 * clears it, or — where that leaves less filler (`leastFillerWidths`) — a
 * wider one the line makes. `{ units }`, or `{ refusal }` when it makes
 * nothing that fits one of them.
 */
function lineSingleUnits({ sorted, lo, hi, run, family, minBox }) {
  const sizes = vanitySinkWidthsIn(family, 1).map((w) => w * IN);
  const territories = territoriesFor(sorted, lo, hi);
  const slots = [];
  for (const [i, b] of sorted.entries()) {
    const t = territories[i];
    const unit = singleUnit(b, t, sizes, run);
    if (!unit.basins) {
      return {
        refusal:
          `${family.vendor || family.name} makes no vanity sink base that ` +
          `fits a ${said(b.bowl)} bowl in ${said(unit.refusedRoom)}`,
      };
    }
    slots.push({
      floor: unit.width,
      room: roomAround(b.centerParam, t),
      place: () => b.centerParam,
    });
  }
  const widths = leastFillerWidths(slots, sizes, lo, hi, minBox);
  return {
    units: sorted.map((b, i) => ({
      basins: [b],
      width: widths[i],
      centerParam: b.centerParam,
      seats: [b.centerParam],
    })),
  };
}

/**
 * The line's sink units for these bowls — `{ units }`, or `{ refusal }` when
 * it makes nothing that fits one of them.
 *
 * Two bowls go in the double-bowl unit wherever both fit inside it AND it
 * leaves no more filler than a single unit each (user ruling 2026-11-03: a
 * 72" double as 6 + 60 + 6 is worse than 36 + 36; a tie keeps the unit, the
 * 2026-10-21 ruling `doubleBowlUnit.js` holds). Otherwise each bowl gets a
 * single unit (`lineSingleUnits`).
 */
function lineSinkUnits({ sorted, lo, hi, run, family }) {
  const minBox = narrowestFillBox(run);
  const singles = lineSingleUnits({ sorted, lo, hi, run, family, minBox });
  const double = lineDoubleUnit(sorted, lo, hi, family);
  if (
    double &&
    (singles.refusal ||
      unitsFiller([double], lo, hi, minBox) <=
        unitsFiller(singles.units, lo, hi, minBox) + TIE)
  ) {
    return { units: [double] };
  }
  return singles;
}

/**
 * The ONE sink-bay rule, with what a line said about it: `vanitySinkBays` is
 * its `bays`.
 *
 * Under a cabinet line with a size catalog (`familySizes`), a STANDARD vanity
 * takes the line's own sink units (`lineSinkUnits`): a bay per unit, one bay
 * for the double-bowl unit carrying `sinkCount: 2` (the fill stamps it on the
 * box, which is how the estimate tells the double from the single 60"). The
 * bowls are never moved by the bays: `applianceParam` is where each unit's
 * first bowl stands, on a derive and on a pick alike — one bay cannot seat two
 * bowls, so a double unit could not honour the pick's re-seat anyway. Where
 * the unit wants them is `seats` (bowl id → param): a BUILDER builds the bowls
 * there before deriving (`lineVanity.seatVanityBasins`), so the derive finds
 * them already seated.
 *
 * Nothing the line makes fits: `refusal` says so, and the bays are the
 * standard rule's. A BUILDER asks first (`lineVanity.seatVanityBasins`) and
 * makes no run of such a vanity at all — it stays one box in the line's
 * family, priced as missing (`build_vanity`). A run that is already in the
 * line keeps the standard bays, at sizes the line does not make, which the
 * estimate prices as missing too. Never quietly a generic vanity.
 *
 * Every other run — no line, or as-built — is exactly the rule it always was
 * (`standardSinkBays`), with no seats and no refusal.
 *
 * @returns {{ bays: Object[], seats: Object<string, number>,
 *   refusal: string|null }}
 */
function vanitySinkPlan(opts) {
  const { basins, lo, hi, sizing, run = null } = opts;
  const family = vanityLineOf(run, sizing);
  const usable = (basins || []).filter(
    (b) => b && b.bowl > 0 && Number.isFinite(b.centerParam)
  );
  if (!usable.length || !family) {
    return { bays: standardSinkBays(opts), seats: {}, refusal: null };
  }
  const sorted = [...usable].sort((a, b) => a.centerParam - b.centerParam);
  const { units, refusal } = lineSinkUnits({ sorted, lo, hi, run, family });
  if (refusal) return { bays: standardSinkBays(opts), seats: {}, refusal };
  const seats = {};
  for (const u of units) {
    u.basins.forEach((b, i) => (seats[b.id] = u.seats[i]));
  }
  return {
    bays: units.map((u) => ({
      id: u.basins[0].id,
      width: u.width,
      centerParam: u.centerParam,
      applianceParam: u.basins[0].centerParam,
      ...(u.basins.length > 1 ? { sinkCount: u.basins.length } : {}),
    })),
    seats,
    refusal: null,
  };
}

/**
 * The widths to reserve for a vanity's sink bases, and where, for a run with
 * no line (or matching what is there).
 *
 * STANDARD sizing: the narrowest standard base that clears each bowl by 3" a
 * side (`sinkBaseWidthFor` — "a sink reserves its BASE CABINET, not the bowl
 * the scan measured"), capped at what its territory can hold while staying
 * centred on the bowl, floored at the bowl itself. The cap is the part a
 * kitchen never needs: 24" is the narrowest standard base and a vanity is
 * routinely narrower. Where what those bases leave is too narrow for any
 * cabinet, each grows to the widest standard base that still stands where it
 * would — centred on its bowl, or in its half when the cabinet leads (a
 * 37.1" vanity takes a 36" base and two 0.55" fillers, not a 24" one between
 * two 6.5" strips) — the standard answer to the leftovers as-built absorbs.
 *
 * AS-BUILT sizing: if EVERY leftover gap is narrower than the narrowest module
 * the family sells, the bases absorb them — each basin takes its whole
 * territory. A scanned vanity IS its boxes, and the alternative is cabinets
 * between scribes: one basin at 36.9" gave a 24" base between two 6.5" strips,
 * and two basins at 63.6" gave two 24" bases and 7.66" + 0.26" + 7.66" of pure
 * filler without a single real cabinet. Above that threshold the run already
 * packs into real cabinets and widening would only lose them, which is why the
 * five other multi-basin corpus runs (0.7"-6.6" of filler beside 3-6 real
 * boxes) are deliberately left alone.
 *
 * Nothing the scan measured moves either way: the basins are incumbents whose
 * footprint the run reserves and never re-emits, so a widened bay changes the
 * CABINET around the bowl and not the bowl (measured — a bay widened to its
 * territory emits no basin at all).
 *
 * @param {Object} opts
 * @param {Array<{id: string, bowl: number, centerParam: number}>} opts.basins
 *   every basin on this run, in any order.
 * @param {number} opts.lo   the run's near end, in the basins' own frame.
 * @param {number} opts.hi   the run's far end.
 * @param {string} opts.sizing `sizingOf(run)`.
 * @param {Object} [opts.run]  for the family's narrowest module — and, in
 *   `vanitySinkPlan`, whether the family is a line with a size catalog; the
 *   default family when absent, which is what the fill will resolve too.
 * @param {boolean} [opts.fromPick] true when the user has just ANSWERED the
 *   sizing question (the depth picker), false when a derive is recording the
 *   vanity that already exists. Only a pick may re-seat a basin — see the
 *   "who leads" note below.
 * @returns {Array<{id: string, width: number, centerParam: number}>} one per
 *   basin, keyed by the id it was given.
 */
function standardSinkBays({
  basins,
  lo,
  hi,
  sizing,
  run = null,
  fromPick = false,
}) {
  const usable = (basins || []).filter(
    (b) => b && b.bowl > 0 && Number.isFinite(b.centerParam)
  );
  if (!usable.length) return [];
  const sorted = [...usable].sort((a, b) => a.centerParam - b.centerParam);
  const territories = territoriesFor(sorted, lo, hi);
  // As-built keeps the standard list it always used: its bases only decide
  // whether the leftovers are absorbed, and a scan's output must not move.
  const sizesIn =
    sizing === AS_BUILT ? SINK_BASE_WIDTHS_IN : vanitySinkSizesIn(run);
  const bases = sorted.map((b, i) =>
    standardBaseIn(
      b.bowl,
      b.centerParam,
      territories[i],
      sinkFloorFor(sizesIn, b.bowl)
    )
  );
  const minBox = narrowestFillBox(run);
  // Who leads, the cabinet or the bowl: see the note in the map below.
  const cabinetLeads = fromPick && sizing !== AS_BUILT && sorted.length > 1;
  const leadOf = (b, t) => (cabinetLeads ? (t.lo + t.hi) / 2 : b.centerParam);

  // AS-BUILT: when every stretch the bases leave behind — the two run ends
  // and each space between neighbours — is narrower than the narrowest
  // module the family sells, the bases absorb them.
  const gaps = gapsOf(
    sorted.map((b, i) => spanOf(bases[i], b.centerParam)),
    lo,
    hi
  );
  const absorb =
    sizing === AS_BUILT &&
    gaps.length > 0 &&
    gaps.every((g) => g < narrowestModuleIn(run) * IN);

  // STANDARD: the sizes the family sells, the one leaving the least filler
  // (`leastFillerWidths`, user ruling 2026-11-03), each base no wider than
  // fits WHERE IT WILL STAND — centred on its bowl, as the standard rule
  // always places a base, or in its half when the cabinet leads (a pick on a
  // double), where the bowl follows it. Without it a 37.1" layout vanity was
  // a 24" base between two 6.5" fillers (two prod bathrooms, 2026-11-03).
  const widths =
    sizing === AS_BUILT
      ? bases
      : leastFillerWidths(
          sorted.map((b, i) => {
            const t = territories[i];
            const lead = leadOf(b, t);
            return {
              floor: bases[i],
              room: cabinetLeads ? t.hi - t.lo : roomAround(b.centerParam, t),
              place: (w) => clamp(lead, t.lo + w / 2, t.hi - w / 2),
            };
          }),
          sizesIn && sizesIn.map((w) => w * IN),
          lo,
          hi,
          minBox
        );

  return sorted.map((b, i) => {
    const t = territories[i];
    if (absorb) {
      // The box is the whole territory, so its centre is the territory's —
      // but the BOWL keeps its own place. Seating it to the box's middle is
      // what moved a scanned double vanity's bowls 3.77" on the first
      // re-solve, as-built, which is the one thing as-built may not do.
      return {
        id: b.id,
        width: t.hi - t.lo,
        centerParam: (t.lo + t.hi) / 2,
        applianceParam: b.centerParam,
      };
    }
    // WHO LEADS, the cabinet or the bowl — the sizing intent again.
    //
    // AS-BUILT: the bowl. It is where the scan found it and the base is built
    // around it.
    //
    // STANDARD with SEVERAL basins, AND ONLY FROM A PICK: the CABINET. The run is divided between
    // the bowls, each half is one standard vanity unit, and a standard unit
    // has its base centred in its half — so the base takes the territory's
    // middle and the basin is seated to it by `moveApplianceObjectsToBays`
    // (product directive 2026-09-27: "when standard size cabinets are chosen,
    // the double vanity sinks have to reposition to match the center of each
    // sink cabinet"). Inheriting the scan's slightly-inboard bowls instead is
    // what left `D6B52714` with 7.66" gaps at both ends and a 0.26" scribe
    // between two bases crammed together in the middle — the scan's asymmetry
    // wearing a standard label.
    //
    // Three gates, and each one is a population that must not move:
    //
    //  - a SINGLE basin has no division to be centred in, and moving a lone
    //    vanity's sink to the middle of a long run is a change nobody asked
    //    for;
    //  - an AS-BUILT basin is where the scan found it, and re-seating it is
    //    the one thing "match what is there" may never do — without this gate
    //    the four double-basin corpus scans that pack into real cabinets moved
    //    their scanned bowls on a re-solve;
    //  - a DERIVE is not an answer. `build_vanity` places its own basins and
    //    is standard by definition, so without `fromPick` the derive re-seated
    //    a vanity the builder had just laid out symmetrically, 1.2" inboard.
    //
    // The directive is about the moment the user CHOOSES standard sizes for a
    // vanity that had been matching the room, which is exactly `fromPick`.
    const lead = leadOf(b, t);
    // Never let that push the base past its territory.
    const half = widths[i] / 2;
    const centerParam = Math.min(Math.max(lead, t.lo + half), t.hi - half);
    // WHERE THE BOWL BELONGS, which is not always the box's middle. A box
    // widened past the bowl's own base has a centre of its own, and under
    // as-built the bowl must stay where the scan put it — so the seat is
    // stated separately and `moveApplianceObjectsToBays` prefers it to the
    // solved box centre. Under a standard PICK it is left out, which is what
    // lets the basin follow its cabinet (the product directive).
    return {
      id: b.id,
      width: widths[i],
      centerParam,
      ...(cabinetLeads ? {} : { applianceParam: b.centerParam }),
    };
  });
}

/**
 * Where a BUILDER builds a vanity's bowls, before it hangs anything over them
 * (user ruling 2026-11-03): a double's in the middle of each half, so each
 * sink base can fill its half — the builder's own placement left 8" of counter
 * at either end and every generic double between strips, and refused a Venus
 * 48" or 54" double outright, its bowls a half-inch too near the middle for
 * the line's narrowest unit centred on them. In a line, where its double-bowl
 * unit holds them instead when, built where the bowls stand, it leaves no
 * more filler than the halves' singles (an 88.7" double: the 60" unit and a
 * real cabinet either end, not two 42" singles between fillers). A single bowl
 * stays where it was built.
 *
 * @param {Object} opts `{ basins, lo, hi, run }` as `vanitySinkPlan` takes.
 * @returns {{ seats: Object<string, number>, refusal: string|null }} a param
 *   for every bowl, and the line's refusal when it makes nothing that fits.
 */
function builderBasinSeats({ basins, lo, hi, run = null }) {
  const usable = (basins || []).filter(
    (b) => b && b.bowl > 0 && Number.isFinite(b.centerParam)
  );
  if (!usable.length) return { seats: {}, refusal: null };
  const sorted = [...usable].sort((a, b) => a.centerParam - b.centerParam);
  const n = sorted.length;
  const halves =
    n > 1
      ? sorted.map((b, i) => ({
          ...b,
          centerParam: lo + ((i + 0.5) * (hi - lo)) / n,
        }))
      : sorted;
  const plan = vanitySinkPlan({
    basins: halves,
    lo,
    hi,
    sizing: STANDARD,
    run,
  });
  const fromHalves = {
    seats: Object.fromEntries(
      halves.map((b) => [b.id, plan.seats[b.id] ?? b.centerParam])
    ),
    refusal: plan.refusal,
  };
  const family = vanityLineOf(run, STANDARD);
  const unit = family ? lineDoubleUnit(sorted, lo, hi, family) : null;
  if (!unit || plan.bays.some((b) => b.sinkCount > 1)) return fromHalves;
  const minBox = narrowestFillBox(run);
  const halvesFiller = fillerOf(
    plan.bays.map((b) => spanOf(b.width, b.centerParam)),
    lo,
    hi,
    minBox
  );
  if (
    !plan.refusal &&
    unitsFiller([unit], lo, hi, minBox) > halvesFiller + TIE
  ) {
    return fromHalves;
  }
  return {
    seats: Object.fromEntries(unit.basins.map((b, i) => [b.id, unit.seats[i]])),
    refusal: null,
  };
}

/**
 * The BOWLS a sink bay holds: its own basin (`applianceId`) and, for a line's
 * double-bowl unit (`sinkCount > 1`), the other basins of its fixture group,
 * which have no bay of their own. ONE reading for every pass that acts on a
 * bay's bowls — the vertical seat after a re-solve (`vanityBasinSeat.js`) and
 * the depth pick's re-plan — so neither can leave the second bowl behind.
 *
 * @returns {Object[]} basin objects, the bay's own first; [] for none.
 */
function bowlsOfSinkBay(bay, objects) {
  const list = objects || [];
  const own = bay?.applianceId
    ? list.find((o) => o?.id === bay.applianceId)
    : null;
  const ownList = own ? [own] : [];
  if (!(bay?.sinkCount > 1) || !bay.fixtureGroupId) return ownList;
  const others = getFixtureGroupSinks(list, bay.fixtureGroupId).filter(
    (o) => o?.id && o.id !== bay.applianceId
  );
  return [...ownList, ...others];
}

/**
 * The widths to reserve for a vanity's sink bases, and where — the ONE rule,
 * for every vanity run: `vanitySinkPlan`'s bays. See `standardSinkBays` for a
 * run with no line (or as built) and `vanitySinkPlan` for one in a line.
 */
function vanitySinkBays(opts) {
  return vanitySinkPlan(opts).bays;
}

module.exports = {
  vanitySinkBays,
  vanitySinkPlan,
  builderBasinSeats,
  sinkBaseWidthForRun,
  bowlsOfSinkBay,
};
